import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Breadcrumb, Card, Descriptions, Table, Tag, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { api, type SensorDetailResp, type SensorInterface } from "../api";

export default function SensorDetail() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<SensorDetailResp | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        setData(await api.sensor(Number(id)));
      } catch (e) {
        message.error(String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // fmtBpsLabel mirrors the helper in SensorForm — Wansight-style short
  // labels (10 Gbps, 900 Mbps, …). Duplicated here so the read-only
  // detail view doesn't pull in the form component.
  function fmtBpsLabel(bps?: number): string {
    if (!bps || bps <= 0) return "—";
    if (bps >= 1_000_000_000 && bps % 1_000_000_000 === 0) return `${bps / 1_000_000_000} Gbps`;
    if (bps >= 1_000_000_000) return `${(bps / 1_000_000_000).toFixed(1)} Gbps`;
    if (bps >= 1_000_000) return `${Math.round(bps / 1_000_000)} Mbps`;
    return `${Math.round(bps / 1_000)} kbps`;
  }

  const columns: ColumnsType<SensorInterface> = [
    { title: "Index", dataIndex: "if_index", width: 80 },
    { title: "Interface Name", dataIndex: "name", ellipsis: true },
    {
      title: "Direction",
      dataIndex: "direction",
      width: 110,
      render: (v: string) => (
        <Tag color={v === "in" ? "blue" : v === "out" ? "orange" : "default"}>
          {v}
        </Tag>
      ),
    },
    {
      title: "Stats",
      dataIndex: "stats_mode",
      width: 100,
      render: (v?: string) => v || "extended",
    },
    {
      title: "Speed IN",
      dataIndex: "speed_in",
      width: 110,
      render: (v?: number) => fmtBpsLabel(v),
    },
    {
      title: "Speed OUT",
      dataIndex: "speed_out",
      width: 110,
      render: (v?: number) => fmtBpsLabel(v),
    },
    {
      title: "Color",
      dataIndex: "color",
      width: 100,
      render: (v: string) =>
        v ? (
          <span
            style={{
              display: "inline-block",
              width: 28,
              height: 14,
              background: v,
              border: "1px solid var(--line)",
              borderRadius: 2,
              verticalAlign: "middle",
            }}
            title={v}
          />
        ) : (
          "—"
        ),
    },
  ];

  return (
    <>
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[{ title: <Link to="/">Dashboard</Link> }, { title: `Sensor ${id}` }]}
      />
      <Card title="Sensor" loading={loading} style={{ marginBottom: 16 }}>
        {data && (
          <Descriptions column={3} bordered size="small">
            <Descriptions.Item label="ID">{data.sensor.id}</Descriptions.Item>
            <Descriptions.Item label="Name">{data.sensor.name}</Descriptions.Item>
            <Descriptions.Item label="Enabled">
              {data.sensor.enabled ? "yes" : "no"}
            </Descriptions.Item>
            <Descriptions.Item label="Protocol">{data.sensor.protocol}</Descriptions.Item>
            <Descriptions.Item label="Listen port">{data.sensor.listen_port}</Descriptions.Item>
            <Descriptions.Item label="Zone ID">{data.sensor.zone_id}</Descriptions.Item>
          </Descriptions>
        )}
      </Card>
      <Card title="Interfaces">
        <Table
          rowKey="if_index"
          loading={loading}
          dataSource={data?.interfaces ?? []}
          columns={columns}
          pagination={false}
          size="middle"
        />
      </Card>
    </>
  );
}
